import {
  Pagination,
  Spinner,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@consigli/facade';
import { useMyUser, useUsers } from '@consigli/hooks';
import { ProjectRole } from '@consigli/types';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoShieldCheckmarkOutline } from 'react-icons/io5';
import { TbCrown } from 'react-icons/tb';

import { formatDisplayName } from '@/util/format-name';

import { UserManagementProjectAccessCard } from './user-management-project-access-card';

interface UserManagementUserListProps {
  search: string;
}

export const UserManagementUserList: FC<UserManagementUserListProps> = ({ search }) => {
  const { t } = useTranslation();
  const { user } = useMyUser();
  const pageSize = 25;
  const [page, setPage] = useState(1);

  const { users, paginatedCount, isFetching } = useUsers({
    page: page,
    pageSize: pageSize,
    search: search || undefined,
  });

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm max-h-[calc(100vh-310px)] overflow-y-auto">
        <Table>
          <TableHead className="sticky top-0 bg-day-light-4">
            <TableRow className="border-b border-gray-100">
              <TableHeaderCell className="pl-6 pr-8 py-3 text-sm font-semibold text-day-neutral-dark">
                {t('user-management.name')}
              </TableHeaderCell>
              <TableHeaderCell className="pr-8 py-3 text-sm font-semibold text-day-neutral-dark">
                {t('user-management.email')}
              </TableHeaderCell>
              <TableHeaderCell className="pr-6 py-3 text-sm font-semibold text-day-neutral-dark">
                {t('user-management.project-access')}
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableDataCell colSpan={3} className="py-12">
                  <div className="w-full flex justify-center">
                    <Spinner />
                  </div>
                </TableDataCell>
              </TableRow>
            ) : (
              users.map((editableUser, index) => (
                <TableRow
                  key={editableUser.id}
                  className={index !== users.length - 1 ? 'border-b border-gray-100' : ''}
                >
                  <TableDataCell className="pl-6 pr-8 py-2.5 text-sm font-medium text-day-neutral-dark truncate max-w-[25vw]">
                    <div className="flex items-center gap-1.5">
                      {editableUser.isSuperuser && (
                        <TbCrown
                          size={16}
                          title={t('user-management.superuser')}
                          className="shrink-0 text-day-neutral-dark"
                        />
                      )}
                      <span className="truncate">
                        {formatDisplayName(editableUser.firstName, editableUser.lastName)}
                      </span>
                    </div>
                  </TableDataCell>
                  <TableDataCell className="pr-8 py-2.5 text-sm text-day-neutral-subtle truncate max-w-[25vw]">
                    {editableUser.email}
                  </TableDataCell>
                  <TableDataCell className="pr-6 py-2.5 max-w-[50vw]">
                    <div className="flex flex-wrap gap-1">
                      {/* Superusers can see all org admins, org admins can only see org admins from the same org */}
                      {editableUser.isOrgAdmin &&
                        (user.isSuperuser ||
                          (user.isOrgAdmin &&
                            user.organizationId === editableUser.organizationId)) && (
                          <UserManagementProjectAccessCard
                            key={editableUser.id}
                            userId={editableUser.id}
                            userName={formatDisplayName(
                              editableUser.firstName,
                              editableUser.lastName,
                            )}
                            name={t('user-management.all-projects')}
                            role={ProjectRole.ADMIN}
                          />
                        )}
                      {editableUser.projectAccess.map((access) => (
                        <UserManagementProjectAccessCard
                          key={access.id}
                          userId={editableUser.id}
                          userName={formatDisplayName(
                            editableUser.firstName,
                            editableUser.lastName,
                          )}
                          projectId={access.id}
                          projectName={access.name}
                          name={access.name}
                          role={access.role}
                        />
                      ))}
                    </div>
                  </TableDataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-4 relative flex items-center justify-center">
        <div className="absolute left-0 flex items-center gap-4">
          <p className="font-bold text-sm text-day-neutral-subtle">
            {paginatedCount} {t('user-management.users')}
          </p>
          <div className="flex">
            <div className="flex mr-4 items-center">
              <div className="bg-night-light-3 h-5 w-6 rounded flex justify-center items-center mr-1">
                <IoShieldCheckmarkOutline
                  size={16}
                  title="ProjectAdminIcon"
                  className="text-night-neutral-dark"
                />
              </div>
              <p className="font-bold text-xs">{t('user-management.project-admin')}</p>
            </div>
            <div className="flex items-center">
              <div className="bg-day-light-2 h-5 w-6 rounded mr-1" />
              <p className="font-bold text-xs">{t('user-management.project-user')}</p>
            </div>
            <div className="flex items-center ml-4">
              <div className="h-5 w-6 flex justify-center items-center mr-1">
                <TbCrown size={16} title="SuperuserIcon" className="text-day-neutral-dark" />
              </div>
              <p className="font-bold text-xs">{t('user-management.superuser')}</p>
            </div>
          </div>
        </div>
        <Pagination
          currentPage={page}
          totalCount={paginatedCount}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </>
  );
};
